import cloneDeep from 'lodash/cloneDeep'

import type { CustomFieldModel, DataStoreItemModel } from '@models/types'

import type { EntitiesState } from '@redux/entities'

import useSelector from './useSelector'

type FilterEntityDataStoreItems = {
  dataStoreItems: EntitiesState['dataStoreItems'],
  entity: {
    type: string,
    id: number,
  },
}

const filterEntityDataStoreItems = (params: FilterEntityDataStoreItems) => {
  const { dataStoreItems, entity } = params

  const array = Object.values(dataStoreItems)
  const filtered = array.filter((dataStoreItem) => {
    const { owner_type, owner_id } = dataStoreItem

    return owner_type === entity.type && owner_id === entity.id
  })

  return filtered
}

type FindDataStoreItemValueForKeyParams = {
  customFieldKey: (string | number | null),
  filteredDataStoreItems: DataStoreItemModel[],
}

const findDataStoreItemValueForKey = (params: FindDataStoreItemValueForKeyParams) => {
  const { customFieldKey, filteredDataStoreItems } = params

  const dataStoreItem = filteredDataStoreItems.find(item => item.key === customFieldKey)

  return dataStoreItem?.value || ''
}

type MergeEntityCustomDataParams = {
  customData: Partial<DataStoreItemModel>,
  entity: object,
}

const mergeEntityCustomData = (params: MergeEntityCustomDataParams) => {
  const { customData, entity } = params

  const mergedData = {
    ...cloneDeep(entity),
    ...customData,
  }

  return mergedData
}

type useEntityCustomDataParams = {
  entity: {
    type: string,
    id: number,
  },
  filteredCustomFields: CustomFieldModel[],
  options?: {
    keyPrefix?: string,
    mergeData?: object,
  },
}

const useEntityCustomData = (params: useEntityCustomDataParams) => {
  const { entity, filteredCustomFields, options } = params
  const { keyPrefix, mergeData } = options || {}

  const entities = useSelector(reduxState => reduxState.entities)
  const { dataStoreItems } = entities

  const filteredDataStoreItems = filterEntityDataStoreItems({ dataStoreItems, entity })

  // Setup Custom Data object
  // { bedrooms: '2', bathrooms: '1' }
  const customData = filteredCustomFields.reduce((acc, customField) => {
    const { key: customFieldKey } = customField

    const key = keyPrefix ? `${keyPrefix}_${customFieldKey}` : customFieldKey

    // eslint-disable-next-line no-param-reassign
    // @ts-expect-error TS7053
    acc[key] = findDataStoreItemValueForKey({ filteredDataStoreItems, customFieldKey })

    return acc
  }, {})

  return {
    customData,
    filteredCustomFields,
    filteredDataStoreItems,
    mergedEntityData: mergeData ? mergeEntityCustomData({ customData, entity }) : {},
  }
}

export default useEntityCustomData
